import React, { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Markdown from './Markdown';
import './BlogPost.css';
import { getPostBySlug } from '../data/blogPosts';
import usePageMeta from '../hooks/usePageMeta';
import JsonLd from './JsonLd';
import { SITE_URL, business } from '../data/business';
import Footer from './Footer';
import BookCallModal from './BookCallModal';

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Always call hooks before any early return.
  usePageMeta(
    post
      ? {
          title: `${post.title} | Second Life Software`,
          description: post.metaDescription,
          path: `/blog/${post.slug}`,
        }
      : {}
  );

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const postUrl = `${SITE_URL}/blog/${post.slug}`;
  // Article schema so search engines and LLM crawlers can attribute, date, and
  // cite the post. dateISO falls back to nothing rather than an invalid date.
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.metaDescription,
    image: `${SITE_URL}/images/og-image.png`,
    ...(post.dateISO ? { datePublished: post.dateISO, dateModified: post.dateISO } : {}),
    author: { '@type': 'Organization', name: post.author || business.name, url: SITE_URL },
    publisher: {
      '@type': 'Organization',
      name: business.name,
      logo: { '@type': 'ImageObject', url: business.logo },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': postUrl },
    url: postUrl,
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
      { '@type': 'ListItem', position: 3, name: post.title, item: postUrl },
    ],
  };

  return (
    <div className="blog-page">
      <JsonLd id={`article-${post.slug}`} data={articleSchema} />
      <JsonLd id={`breadcrumb-${post.slug}`} data={breadcrumbSchema} />
      <header className="blog-topbar">
        <Link to="/" className="blog-logo">Second Life Software</Link>
        <button className="blog-topbar-btn" onClick={() => setIsModalOpen(true)}>
          Book a Call
        </button>
      </header>

      <article className="blog-post">
        <div className="blog-post-container">
          <Link to="/blog" className="blog-post-back">← All articles</Link>

          {post.tag && <span className="blog-post-tag">{post.tag}</span>}
          <h1 className="blog-post-title">{post.title}</h1>
          <div className="blog-post-meta">
            <span>{post.date}</span>
            <span>·</span>
            <span>{post.readTime}</span>
          </div>

          <div className="blog-post-body">
            <Markdown>{post.body}</Markdown>
          </div>

          <div className="blog-post-cta">
            <h3 className="blog-post-cta-title">Have an idea you're ready to build?</h3>
            <p className="blog-post-cta-text">
              We'll help you scope it down to a launchable first version — and give you an
              honest, jargon-free estimate. No obligation.
            </p>
            <button className="blog-post-cta-btn" onClick={() => setIsModalOpen(true)}>
              Book a Free Consultation
            </button>
          </div>
        </div>
      </article>

      <Footer />
      <BookCallModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default BlogPost;
